import type { FieldType } from "./types";
import type { RuleConditionKey } from "./state/formVersion";
import { RuleActionKey } from "./state/formVersion";
import type { VariableType } from "./variables/types";
import type { SelectableIconName } from "./utils/iconUtil";
import type { LanguageCode } from "./state/i18n";

export const AUTH_APP_ID = "moreapp-portal";
export const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL;
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL;
export const LEGACY_PLATFORM_BASE_URL = import.meta.env.VITE_LEGACY_PLATFORM_BASE_URL;
export const COUNTRIES_BASE_URL = import.meta.env.VITE_COUNTRIES_BASE_URL;
export const LOG_COLLECTOR_URL = import.meta.env.VITE_LOG_COLLECTOR_URL;
export const COMMIT_SHA = import.meta.env.VITE_COMMIT_SHA;
export const LOCATION_BASE_URL = import.meta.env.VITE_AUTH_LOCATION_BASE_URL;

export const APP_STORE_URL = "https://apps.apple.com/app/moreapp-forms/id839543670";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.moreapp";

export const USER_INPUT_DEBOUNCE = 250;
export const MINIMUM_ACTION_LOADING_DELAY = 350;
export const MINIMUM_PAGE_LOADING_DELAY = 1500;
export const SLOW_SUBMIT_DELAY = 2000;
export const COPY_SUCCESS_MESSAGE_DURATION_MS = 3000;

export const CUSTOMER_DELETION_DELAY_DAYS = 30;

export const MAX_PHOTOS = 25;

export const TRIAL_SUBMISSION_LIMIT = 75;

export const MAX_NUMBER_XS = 100;
export const MAX_NUMBER_SM = 300;
export const MAX_NUMBER_XL = 9000000000000000;
export const MAX_NUMBER_SM_NEGATIVE = -300;
export const MAX_NUMBER_MD_NEGATIVE = -4096;
export const MAX_NUMBER_XL_NEGATIVE = -9000000000000000;
export const MAX_CHARACTERS_XS = 64;
export const MAX_CHARACTERS_SM = 300;
export const MAX_CHARACTERS_MD = 500;
export const MAX_CHARACTERS_LG = 1500;
export const MAX_CHARACTERS_XL = 32000;
export const MAX_CHARACTERS_2XL = 100000;

export const MAX_SEARCH_RESULTS_PER_CATEGORY = 10;
export const MAX_LAST_EDITED_FORMS = 3;

export const MAX_CHARACTERS_PHONE_NUMBER = 15;
export const MIN_CHARACTERS_PASSWORD = 8;
export const MAX_CHARACTERS_PASSWORD = 255;
export const MAX_CHARACTERS_INVOICE_FIELD_LABEL = 40;
export const MAX_CHARACTERS_INVOICE_FIELD_VALUE = 140;

export const PORT_MIN_VALUE = 1;
export const PORT_MAX_VALUE = 65535;

export const MIN_RATE_SCALE = 2;
export const MAX_RATE_SCALE = 10;
export const MAX_IMAGE_HEIGHT = 4096;
export const MAX_IMAGE_WIDTH = 4096;

export const MAX_VAT = 100;

export const MAX_DISPLAYED_BANNERS = 2;
export const MAX_FOLDERS = 1000;
export const MAX_DATA_SOURCES = 1000;
export const MAX_DATA_SOURCE_FAILURE_COUNT = 5;
export const MAX_DATA_SOURCE_SIZE = 1024 * 1024 * 10; // 10MiB
export const URL_DATA_SOURCE_MAX_PARAMETERS = 10;
export const URL_DATA_SOURCE_MAX_REQUEST_HEADERS = 10;
export const MAX_GROUPS = 100;
export const MAX_API_KEYS = 20;
export const MAX_WEBHOOKS = 100;
export const MAX_SERVICE_ACCOUNTS = 50;
export const MAX_CUSTOM_ROLES = 50;
export const MAX_RULES = 200;
export const MAX_RULE_ACTIONS = 50;
export const MAX_RULE_CONDITIONS = 50;
export const MAX_INTEGRATIONS = 20;
export const MAX_RESOURCES_LIMIT = 2000;
export const MAX_OPTIONS = 50;
export const MAX_PINS = 10;
export const MAX_COLOURS = 20;
export const MAX_DECIMAL_PRECISION = 10;
export const MAX_CALCULATION_TERMS = 100;

export const MAX_TOTAL_RECIPIENTS = 35;
export const MAX_WORD_INTEGRATION_TEMPLATE_SIZE = 1024 * 1024 * 15; // 15MiB
export const MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE = 1024 * 1024 * 15; // 15MiB

export const MIME_TYPE_EXCEL = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const MIME_TYPE_WORD = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const ALLOWED_CONDITION_FIELD_TYPES: FieldType[] = [
  "text",
  "textArea",
  "readOnlyText",
  "radio",
  "checkbox",
  "lookup",
  "smiley",
  "rating",
  "video",
  "file",
  "photo",
  "drawing",
  "pin",
  "email",
  "phone",
  "signature",
  "date",
  "time",
  "dateTime",
  "timeDifference",
  "stopwatch",
  "search",
  "catalogue",
  "barcode",
  "iban",
  "licensePlate",
  "number",
  "slider",
  "calculation",
  "price",
  "location",
  "currentLocation",
  "address",
  "subform",
];

export const ALLOWED_CONDITION_VARIABLE_TYPES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

export const ACTION_FIELD_VALUE_TYPES: FieldType[] = ["date", "time", "dateTime"];

export const VALUE_FIELD_REFERENCE_KEYS: RuleActionKey[] = [RuleActionKey.REFERENCE, RuleActionKey.VALUE];
export const VALUE_ACTION_KEYS: RuleActionKey[] = [RuleActionKey.VALUE];
export const VISIBILITY_ACTION_KEYS: RuleActionKey[] = [RuleActionKey.HIDDEN, RuleActionKey.VISIBLE];

export const BOOLEAN_CONDITION_KEYS: RuleConditionKey[] = ["is", "hasValue"];
export const EXISTS_CONDITION_KEYS: RuleConditionKey[] = ["hasValue"];
export const PREDEFINED_CONDITION_KEYS: RuleConditionKey[] = ["is", "hasValue", "contains"];
export const OBJECT_CONDITION_KEYS: RuleConditionKey[] = ["is", "contains", "hasValue"];
export const TEXT_CONDITION_KEYS: RuleConditionKey[] = ["is", "startsWith", "endsWith", "contains", "hasValue"];
export const USER_CONDITION_KEYS: RuleConditionKey[] = ["is", "startsWith", "endsWith", "contains", "hasValue"];
export const NUMBER_RANGE_CONDITION_KEYS: RuleConditionKey[] = ["is", "hasValue", "lessThan", "greaterThan"];
export const NUMBER_CONDITION_KEYS: RuleConditionKey[] = [
  "is",
  "startsWith",
  "endsWith",
  "contains",
  "hasValue",
  "greaterThan",
  "lessThan",
];

export const DECORATIVE_IMAGE_ALT = "";

export const IGNORE_PASSWORD_MANAGER_PROPS = {
  // https://stackoverflow.com/questions/41945535/html-disable-password-manager
  autocomplete: "off",
  // Password managers can ignore the `autocomplete` attribute
  "data-1p-ignore": true, // 1Password
  "data-bwignore": true, // Bitwarden
  "data-lpignore": true, // LastPass
  "data-protonpass-ignore": true, // ProtonPass
  "data-form-type": "other", // Dashlane
};

const SELECTABLE_ICONS: SelectableIconName[] = [
  "academicCap",
  "adjustments",
  "archive",
  "arrowCircleDown",
  "arrowCircleLeft",
  "arrowCircleRight",
  "arrowCircleUp",
  "arrowDown",
  "arrowLeft",
  "arrowRight",
  "arrowUp",
  "arrowsExpand",
  "atSymbol",
  "backspace",
  "beaker",
  "bell",
  "bookOpen",
  "bookmark",
  "bookmarkAlt",
  "briefcase",
  "calculator",
  "calendar",
  "camera",
  "cash",
  "chartBar",
  "chartPie",
  "chat",
  "chatAlt",
  "chatAlt_2",
  "check",
  "checkCircle",
  "chevronDown",
  "chevronLeft",
  "chevronRight",
  "chevronUp",
  "clock",
  "cloud",
  "cloudDownload",
  "cloudUpload",
  "code",
  "cog",
  "collection",
  "creditCard",
  "cube",
  "currencyDollar",
  "currencyEuro",
  "currencyYen",
  "deviceMobile",
  "deviceTablet",
  "documentDuplicate",
  "dotsHorizontal",
  "dotsVertical",
  "download",
  "duplicate",
  "emojiHappy",
  "emojiSad",
  "exclamation",
  "exclamationCircle",
  "eye",
  "eyeOff",
  "film",
  "filter",
  "fire",
  "flag",
  "hand",
  "hashtag",
  "heart",
  "home",
  "inbox",
  "inboxIn",
  "informationCircle",
  "key",
  "lightBulb",
  "lightningBolt",
  "link",
  "locationMarker",
  "lockClosed",
  "lockOpen",
  "login",
  "logout",
  "mail",
  "mailOpen",
  "map",
  "menu",
  "microphone",
  "minus",
  "minusCircle",
  "moon",
  "musicNote",
  "paperAirplane",
  "paperClip",
  "pause",
  "pencil",
  "pencilAlt",
  "phone",
  "photograph",
  "play",
  "plus",
  "plusCircle",
  "printer",
  "qrcode",
  "questionMarkCircle",
  "refresh",
  "reply",
  "rss",
  "scissors",
  "search",
  "share",
  "shoppingBag",
  "shoppingCart",
  "speakerphone",
  "star",
  "statusOnline",
  "stop",
  "sun",
  "support",
  "switchHorizontal",
  "tag",
  "thumbDown",
  "thumbUp",
  "trash",
  "trendingDown",
  "trendingUp",
  "upload",
  "user",
  "userAdd",
  "userGroup",
  "users",
  "videoCamera",
  "viewGrid",
  "viewList",
  "volumeOff",
  "volumeUp",
  "wifi",
  "x",
  "xCircle",
];

export const SELECTABLE_FOLDER_ICONS: SelectableIconName[] = ["folder", ...SELECTABLE_ICONS];
export const SELECTABLE_FORM_ICONS: SelectableIconName[] = ["document", ...SELECTABLE_ICONS];

export const GRIDFS_FILE_PREFIX = "gridfs://registrationFiles/";

export const WEBHOOK_EVENT_TYPES = [
  "submission.created",
  "submission.deleted",
  "submission.pipeline.created",
  "submission.pipeline.blocked",
  "submission.pipeline.failed",
  "submission.pipeline.succeeded",
  "submission.pipeline.mail.sent",
  "submission.task.created",
  "submission.task.fulfilled",
  "submission.export.scheduled",
  "submission.export.started",
  "submission.export.finished",
  "datasource.import.failed",
  "user.added",
];

export const LANGUAGE_OPTIONS = [
  { key: "en", nativeLabel: "English" },
  { key: "nl", nativeLabel: "Nederlands" },
  { key: "es", nativeLabel: "Español" },
  { key: "de", nativeLabel: "Deutsch" },
  { key: "fr", nativeLabel: "Français" },
  { key: "pt", nativeLabel: "Português" },
  { key: "ca", nativeLabel: "Català" },
] as { key: LanguageCode; nativeLabel: string }[];
